/**
 * Portal session client, built against the amended API contract
 * (ADR-20261005-p7pz, api_changes 2026-10-05): POST /api/portal/sign-in,
 * POST /api/portal/magic-link/redeem and POST /api/portal/session/refresh.
 * Hardened by TG-3 (m1-magic-link-portal-refresh) against TG-1's own
 * session-hardening changes in the same feature: the `active_session`
 * redeem outcome, the redeem confirm timeout, and `refreshSession`'s
 * three-way outcome that only ever ends a session on the refresh
 * endpoint's own 401 (ADR-20261015-6576, ADR-20261015-6w29).
 *
 * `authenticatedFetch` lives in `./authenticated-fetch` -- this file owns
 * the plain, single-attempt HTTP shape of each endpoint; that one owns the
 * retry, single-flight and session-ended bookkeeping built on top of
 * `refreshSession` below.
 */

import { setSessionToken } from "./token";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

function readTokenField(body: unknown): string | null {
  if (
    typeof body === "object" &&
    body !== null &&
    "token" in body &&
    typeof (body as { token: unknown }).token === "string"
  ) {
    return (body as { token: string }).token;
  }
  return null;
}

function readStringField(body: unknown, field: string): string | null {
  if (typeof body === "object" && body !== null && field in body) {
    const value = (body as Record<string, unknown>)[field];
    if (typeof value === "string") {
      return value;
    }
  }
  return null;
}

async function readJsonBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export type SignInOutcome = "accepted" | "error";

export interface SignInResult {
  outcome: SignInOutcome;
}

/**
 * ADR-20261015-xn9h: the re-issue origin marker. The sign-in route carries
 * `?origin=link-unavailable` when the visitor arrived from the
 * link-unavailable view, so the processor can pick the re-issue email
 * template. The marker grants nothing and the 202 response stays identical.
 * Query parameter, not router state, so a refresh keeps it.
 */
export const SIGN_IN_ORIGIN_PARAM = "origin";
export const LINK_UNAVAILABLE_ORIGIN = "link-unavailable";
export type SignInOrigin = typeof LINK_UNAVAILABLE_ORIGIN;

/** Maps a raw query string to a known origin; anything else is absent. */
export function readSignInOrigin(search: string): SignInOrigin | undefined {
  const value = new URLSearchParams(search).get(SIGN_IN_ORIGIN_PARAM);
  return value === LINK_UNAVAILABLE_ORIGIN ? LINK_UNAVAILABLE_ORIGIN : undefined;
}

/**
 * ADR-20261102-k4rf (m1-sign-in-renewal-scope AC-5): the outreach email's
 * sign-in link carries `?r=<ref>`, an opaque reference to the renewal the
 * email was about. Forwarded as `renewal` so the processor mints only that
 * renewal's link. It grants nothing, and a malformed value is treated as
 * absent, so the body is exactly today's.
 */
export const SIGN_IN_REF_PARAM = "r";
const SIGN_IN_REF_PATTERN = /^[A-Za-z0-9_-]{22}$/;

/** Returns a well-formed renewal reference from a raw query string, or undefined. */
export function readSignInRef(search: string): string | undefined {
  const value = new URLSearchParams(search).get(SIGN_IN_REF_PARAM);
  return value !== null && SIGN_IN_REF_PATTERN.test(value) ? value : undefined;
}

/** `path`, with `?r=<ref>` added when `search` carries a well-formed reference. */
export function withSignInRef(path: string, search: string): string {
  const ref = readSignInRef(search);
  return ref === undefined ? path : `${path}?${SIGN_IN_REF_PARAM}=${ref}`;
}

/**
 * AC-1: the 202 response is byte-identical across a match, several
 * matches, no match, revoked access and a capped address -- this client
 * reads nothing from that body beyond the status code. Recovering more
 * than "accepted" from it would recreate exactly the disclosure the
 * design removes.
 *
 * The body is `{ email }`, plus `origin` when a known origin marker is
 * supplied (ADR-20261015-xn9h) and `renewal` when a renewal reference is
 * supplied (ADR-20261102-k4rf).
 */
export async function requestSignIn(
  email: string,
  origin?: SignInOrigin,
  renewal?: string,
): Promise<SignInResult> {
  // AC-21: any non-202 status (400 included) is "error", never a format error -- the caller
  // shows check-your-email for every outcome once its own syntax check has passed.
  const response = await fetch("/api/portal/sign-in", {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({
      email,
      ...(origin === undefined ? {} : { origin }),
      ...(renewal === undefined ? {} : { renewal }),
    }),
  });

  if (response.status === 202) {
    return { outcome: "accepted" };
  }
  return { outcome: "error" };
}

/**
 * ADR-20261008-181w, extended by ADR-20261015-6w29: the redeem outcome
 * mapping.
 *  - `authenticated` / `read_only`: a 200 response, distinguished by its
 *    `outcome` field (AC-3's second sentence).
 *  - `active_session`: a 200 `{outcome: "active_session"}` response --
 *    the presented token was spent or unknown, but the browser already
 *    holds a live session, so the server made no change at all (AC-9).
 *    Carries no token; this module establishes no new session for it.
 *  - `not_found`: the opaque not-found 303 (AC-6, AC-7), or any other 4xx
 *    other than 429 (AC-8) -- never distinguished further, by design.
 *  - `throttled`: a 429 response (AC-8).
 *  - `unavailable`: a 5xx response, the confirm timing out, or the fetch
 *    itself failing (AC-8, 1.6) -- a transport failure is caught here,
 *    not left for the caller.
 */
export type RedeemOutcome = "authenticated" | "read_only" | "active_session" | "not_found" | "throttled" | "unavailable";

export interface RedeemResult {
  outcome: RedeemOutcome;
}

/** ADR-20261015-6w29: the confirm POST's own client-side timeout -- long
 * enough for a cold Function's Key Vault signing round trip, short enough
 * to match Front Door's own origin-timeout headroom. A request that has
 * not settled by this point is aborted and reported the same way a 5xx or
 * a network drop is (`unavailable`, state 1.6, AC-8). */
const REDEEM_CONFIRM_TIMEOUT_MS = 10_000;

/**
 * `redirect: "manual"` is deliberate, not the fetch default of "follow".
 * AC-6/AC-7 route every not-found case through a server-side 303 to a page
 * this same SPA also serves (`/sign-in/link-unavailable` or the caller's
 * own existing journey); a following fetch() would quietly turn that 303
 * into a 200 carrying the redirected page's own HTML, and this function
 * would misread it as a successful redemption. Manual mode surfaces the
 * redirect as an opaque, zero-status response instead
 * (`response.type === "opaqueredirect"`), which this function reads as
 * not_found without attempting to recover the Location target -- which
 * manual mode does not expose even for a same-origin request, and which
 * AC-7 requires to stay indistinguishable across every not-found variant
 * in any case.
 *
 * A thrown fetch (no response at all -- a network drop, or this
 * function's own {@link REDEEM_CONFIRM_TIMEOUT_MS} abort) is caught here
 * and mapped to `unavailable`, the same outcome a 5xx response takes,
 * rather than left to escape as an unhandled rejection the caller would
 * have to guard against separately (AC-8: "a confirm request that does
 * not complete within the timeout").
 */
export async function redeemMagicLink(token: string): Promise<RedeemResult> {
  const timeoutController = new AbortController();
  const timeoutHandle = setTimeout(() => timeoutController.abort(), REDEEM_CONFIRM_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch("/api/portal/magic-link/redeem", {
      method: "POST",
      credentials: "include",
      referrerPolicy: "no-referrer",
      redirect: "manual",
      headers: JSON_HEADERS,
      body: JSON.stringify({ token }),
      signal: timeoutController.signal,
    });
  } catch {
    return { outcome: "unavailable" };
  } finally {
    clearTimeout(timeoutHandle);
  }

  if (response.type === "opaqueredirect") {
    return { outcome: "not_found" };
  }
  if (response.status === 429) {
    return { outcome: "throttled" };
  }
  if (response.status >= 500) {
    return { outcome: "unavailable" };
  }
  if (response.status === 200) {
    const body = await readJsonBody(response);
    // ADR-20261015-6w29 (AC-9): checked before the token field, since an
    // active_session response carries no token at all -- reading it first
    // would otherwise fall through to the not-found branch below for this
    // one 200 shape.
    if (readStringField(body, "outcome") === "active_session") {
      return { outcome: "active_session" };
    }
    const sessionToken = readTokenField(body);
    if (sessionToken !== null) {
      setSessionToken(sessionToken);
      return { outcome: readStringField(body, "outcome") === "read_only" ? "read_only" : "authenticated" };
    }
  }
  // Any other 4xx (AC-8: "any other 4xx"), or a 200 carrying no readable
  // token, takes the same not-found outcome as the opaque redirect above.
  return { outcome: "not_found" };
}

/**
 * ADR-20261015-6576: the outcome of ONE refresh attempt -- never retried
 * and never single-flighted here, both of which are
 * `authenticated-fetch.ts`'s job, built on top of this function.
 *  - `refreshed`: a 200 with a readable token. The in-memory session is
 *    updated.
 *  - `ended`: the refresh endpoint's own 401 -- AC-25's one, explicit
 *    session-ending condition. The caller clears the session; this
 *    function does not, so a caller mid-retry never has to re-establish
 *    state this function already cleared out from under it.
 *  - `retryable`: anything else -- a 5xx, a 429, a gateway error, an
 *    opaque manual-mode redirect, a network failure, or a 200 with no
 *    readable token. AC-25 is explicit that the session survives every
 *    one of these, so none of them may be folded into `ended`.
 */
export type RefreshAttemptOutcome = "refreshed" | "ended" | "retryable";

/**
 * Attempts one refresh from the `__Host-sbr_refresh` cookie, sent
 * automatically by the browser (`credentials: "include"`) -- this module
 * never reads or sets that cookie directly, only the server does.
 */
export async function refreshSession(): Promise<RefreshAttemptOutcome> {
  let response: Response;
  try {
    response = await fetch("/api/portal/session/refresh", {
      method: "POST",
      credentials: "include",
      redirect: "manual",
    });
  } catch {
    return "retryable";
  }

  if (response.status === 401) {
    return "ended";
  }
  if (response.status === 200) {
    const sessionToken = readTokenField(await readJsonBody(response));
    if (sessionToken !== null) {
      setSessionToken(sessionToken);
      return "refreshed";
    }
  }
  return "retryable";
}

// `submitRenewal` (AC-10, AC-12, AC-27) lives in `./authenticated-fetch`,
// not here: it is the one function in this module's public surface that
// needs the retrying `authenticatedFetch` wrapper, and that wrapper
// itself needs `refreshSession` above -- keeping `submitRenewal` there
// instead of here keeps the import edge between the two files one
// directional (authenticated-fetch.ts -> api.ts) rather than circular.
